import { truncate } from "lodash";

import { formatInteractionDisplayDate, getInteractionDisplayName } from "@/components/interactions/_utils";
import { InteractionMode } from "@/components/interactions/types";

import type { CareerPlanRoleRubric } from "../queries";
import {
  CONSULTANT_DIRECTION_LABELS,
  CONSULTANT_SUMMARY_LABELS,
  formatCatalogValue,
  formatRankedLabels,
  inferDirections,
  type CareerIntentCatalogDomain,
  type CareerIntentId
} from "./careerIntentCatalog";
import { getExperienceIndexCompletion, getExperienceIndexHubState } from "./careerPlanHubSummaries";
import type { CareerPlanAssessment, CareerPlanGoal } from "./careerPlanHistory";
import {
  formatExperienceIndexScoreValue,
  getCareerRoleFits,
  getHubAreasToDiscuss,
  getHubTopStrengths,
  type CareerRoleFit,
  type ExperienceIndexHubCompetency
} from "./experienceIndexResults";
import type { CareerPlanOnboardingInteraction } from "./queries";

export const CAREER_PLAN_ONBOARDING_EXPERIENCE_INDEX_LIMIT = 5;

export const CAREER_PLAN_ONBOARDING_FOCUS_AREAS_DESCRIPTION =
  "Focus areas adjust based on the comparison role to reflect the largest gaps from that role.";

export type CareerPlanOnboardingFormValues = {
  selectedInteractionIds: string[];
  consultantNotes: string;
  selectedCareerGoalIds: string[];
  selectedExperienceIndexIds: string[];
  comparisonRoleId: string;
};

export function emptyCareerPlanOnboardingFormValues(): CareerPlanOnboardingFormValues {
  return {
    selectedInteractionIds: [],
    consultantNotes: "",
    selectedCareerGoalIds: [],
    selectedExperienceIndexIds: [],
    comparisonRoleId: ""
  };
}

export function selectedInteractionCountLabel(selected: number, total: number): string {
  return `${selected} of ${total} selected`;
}

export function consultantNotesStepLabel(notes: string): string {
  return notes.trim() ? "Note added" : "No note yet";
}

const ACTIVE_INTENT_IDS: CareerIntentId[] = ["improve", "new_role", "location", "facility", "retirement"];

export type CareerPlanOnboardingGoalGroupId =
  | "exploring"
  | "improvement"
  | "targetPositions"
  | "states"
  | "facilityTypes"
  | "retirement"
  | "motivations"
  | "timeframe"
  | "clarity"
  | "notes";

export type CareerPlanOnboardingGoalItem = {
  id: string;
  label: string;
};

export type CareerPlanOnboardingGoalGroup = {
  id: CareerPlanOnboardingGoalGroupId;
  label: string;
  items: CareerPlanOnboardingGoalItem[];
  presentation: "chips" | "note";
};

export function careerPlanOnboardingGoalItemId(groupId: CareerPlanOnboardingGoalGroupId, value: string): string {
  return `${groupId}:${value}`;
}

export function careerPlanOnboardingGoalItemIds(groups: CareerPlanOnboardingGoalGroup[]): string[] {
  return groups.flatMap((group) => group.items.map((item) => item.id));
}

export function toggleCareerPlanOnboardingSelectedId(selected: string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id];
}

export function toggleCareerPlanOnboardingGoalId(selected: string[], id: string): string[] {
  return toggleCareerPlanOnboardingSelectedId(selected, id);
}

export type CareerPlanOnboardingExperienceIndexGroupId = "strengths" | "focusAreas";

export type CareerPlanOnboardingExperienceIndexItem = {
  id: string;
  label: string;
  score: number;
  target: number | null;
  gap: number | null;
};

export type CareerPlanOnboardingExperienceIndexGroup = {
  id: CareerPlanOnboardingExperienceIndexGroupId;
  label: string;
  description?: string;
  items: CareerPlanOnboardingExperienceIndexItem[];
};

export function careerPlanOnboardingExperienceIndexItemId(
  groupId: CareerPlanOnboardingExperienceIndexGroupId,
  competencyId: string
): string {
  return `${groupId}:${competencyId}`;
}

export function careerPlanOnboardingExperienceIndexItemIds(
  groups: CareerPlanOnboardingExperienceIndexGroup[]
): string[] {
  return groups.flatMap((group) => group.items.map((item) => item.id));
}

export function reconcileCareerPlanOnboardingSelectedIds(
  selected: string[],
  previousIds: string[],
  nextIds: string[]
): string[] {
  const selectedSet = new Set(selected);
  const previousSet = new Set(previousIds);
  return nextIds.filter((id) => selectedSet.has(id) || !previousSet.has(id));
}

export function getCareerPlanOnboardingComparisonRoleFit(
  assessment: Pick<CareerPlanAssessment, "assessmentComparisonRole" | "scores"> | null | undefined,
  rubrics: CareerPlanRoleRubric[]
): CareerRoleFit | null {
  const roleName = assessment?.assessmentComparisonRole?.trim();
  if (!roleName) return null;

  return (
    getCareerRoleFits(rubrics, assessment?.scores ?? []).find(
      (fit) => fit.title === roleName || fit.classificationLabel === roleName
    ) ?? null
  );
}

export function sortCareerPlanOnboardingRoleTitles(left: string, right: string): number {
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
}

function sortFocusAreaItems(
  items: CareerPlanOnboardingExperienceIndexItem[]
): CareerPlanOnboardingExperienceIndexItem[] {
  if (items.every((item) => item.gap == null)) return items;

  return [...items].sort(
    (left, right) =>
      (right.gap ?? Number.NEGATIVE_INFINITY) - (left.gap ?? Number.NEGATIVE_INFINITY) ||
      sortCareerPlanOnboardingRoleTitles(left.label, right.label)
  );
}

function remainingCompetenciesAfterStrengths(
  scores: NonNullable<CareerPlanAssessment["scores"]>,
  strengths: ExperienceIndexHubCompetency[]
): ExperienceIndexHubCompetency[] {
  const strengthIds = new Set(strengths.map((item) => item.id));
  return scores.flatMap((score) => {
    if (!score.isComplete || typeof score.score !== "number" || strengthIds.has(score.competencyId)) {
      return [];
    }

    return [
      {
        id: score.competencyId,
        label: score.competencyName.trim() || "Experience",
        score: score.score
      }
    ];
  });
}

function getOnboardingFocusAreas(
  scores: NonNullable<CareerPlanAssessment["scores"]>,
  roleFit: CareerRoleFit | null | undefined,
  strengths: ExperienceIndexHubCompetency[]
): ExperienceIndexHubCompetency[] {
  if (!roleFit) {
    return getHubAreasToDiscuss(scores, CAREER_PLAN_ONBOARDING_EXPERIENCE_INDEX_LIMIT);
  }

  const desiredByCompetencyId = new Map(
    roleFit.competencyScores.map((skillScore) => [skillScore.competencyId, skillScore.desiredScore])
  );

  return remainingCompetenciesAfterStrengths(scores, strengths)
    .map((item) => {
      const target = desiredByCompetencyId.get(item.id);
      const gap = target == null ? null : Math.max(target - item.score, 0);
      return { item, gap };
    })
    .filter((entry) => (entry.gap ?? 0) > 0)
    .sort(
      (left, right) =>
        (right.gap ?? 0) - (left.gap ?? 0) || sortCareerPlanOnboardingRoleTitles(left.item.label, right.item.label)
    )
    .slice(0, CAREER_PLAN_ONBOARDING_EXPERIENCE_INDEX_LIMIT)
    .map((entry) => entry.item);
}

export function careerPlanOnboardingExperienceIndexGapLabel(gap: number | null): string | undefined {
  if (gap == null) return undefined;
  return `Gap: ${formatExperienceIndexScoreValue(gap)}`;
}

export function getCareerPlanOnboardingExperienceIndexGroups(
  assessment: CareerPlanAssessment | null | undefined,
  roleFit?: CareerRoleFit | null
): CareerPlanOnboardingExperienceIndexGroup[] {
  const hubState = getExperienceIndexHubState(getExperienceIndexCompletion(assessment));
  if (hubState !== "finished") return [];

  const scores = assessment?.scores ?? [];
  const groups: CareerPlanOnboardingExperienceIndexGroup[] = [];
  const strengths = getHubTopStrengths(scores, CAREER_PLAN_ONBOARDING_EXPERIENCE_INDEX_LIMIT);
  const focusAreas = getOnboardingFocusAreas(scores, roleFit, strengths);
  const desiredByCompetencyId = new Map(
    roleFit?.competencyScores.map((skillScore) => [skillScore.competencyId, skillScore.desiredScore]) ?? []
  );

  const toItems = (
    groupId: CareerPlanOnboardingExperienceIndexGroupId,
    items: Array<{ id: string; label: string; score: number }>
  ): CareerPlanOnboardingExperienceIndexItem[] =>
    items.map((item) => {
      const target = desiredByCompetencyId.get(item.id) ?? null;
      return {
        id: careerPlanOnboardingExperienceIndexItemId(groupId, item.id),
        label: item.label,
        score: item.score,
        target,
        gap: target == null ? null : Math.max(target - item.score, 0)
      };
    });

  if (strengths.length > 0) {
    groups.push({
      id: "strengths",
      label: "Strengths",
      items: toItems("strengths", strengths)
    });
  }

  if (focusAreas.length > 0) {
    groups.push({
      id: "focusAreas",
      label: "Focus Areas",
      description: CAREER_PLAN_ONBOARDING_FOCUS_AREAS_DESCRIPTION,
      items: sortFocusAreaItems(toItems("focusAreas", focusAreas))
    });
  }

  return groups;
}

function catalogItems(
  groupId: CareerPlanOnboardingGoalGroupId,
  domain: CareerIntentCatalogDomain,
  values: Array<string | number | null | undefined> | null | undefined
): CareerPlanOnboardingGoalItem[] {
  const items = (values ?? []).flatMap((value) => {
    if (value == null || value === "") return [];
    const label = formatCatalogValue(domain, value);
    if (!label) return [];
    return [{ id: careerPlanOnboardingGoalItemId(groupId, String(value)), label }];
  });

  const rankedLabels = formatRankedLabels(items.map((item) => item.label));
  return items.map((item, index) => ({ ...item, label: rankedLabels[index] ?? item.label }));
}

export function getCareerPlanOnboardingGoalGroups(
  goal: CareerPlanGoal | null | undefined
): CareerPlanOnboardingGoalGroup[] {
  if (!goal) return [];

  const selectedIntents = inferDirections(goal);
  const active = selectedIntents.filter((intent) => ACTIVE_INTENT_IDS.includes(intent));
  const groups: CareerPlanOnboardingGoalGroup[] = [];

  const addChipGroup = (id: CareerPlanOnboardingGoalGroupId, label: string, items: CareerPlanOnboardingGoalItem[]) => {
    if (items.length === 0) return;
    groups.push({ id, label, presentation: "chips", items });
  };

  if (selectedIntents.length > 0) {
    addChipGroup(
      "exploring",
      CONSULTANT_SUMMARY_LABELS.exploring,
      selectedIntents.map((intent) => ({
        id: careerPlanOnboardingGoalItemId("exploring", intent),
        label: CONSULTANT_DIRECTION_LABELS[intent]
      }))
    );
  }

  if (active.includes("improve")) {
    addChipGroup(
      "improvement",
      CONSULTANT_SUMMARY_LABELS.improvement,
      catalogItems("improvement", "improvementArea", goal.improvementAreas)
    );
  }

  if (active.includes("new_role")) {
    addChipGroup(
      "targetPositions",
      CONSULTANT_SUMMARY_LABELS.targetPositions,
      catalogItems("targetPositions", "targetPosition", goal.targetPositions)
    );
  }

  if (active.includes("location")) {
    addChipGroup("states", CONSULTANT_SUMMARY_LABELS.states, catalogItems("states", "location", goal.locations));
  }

  if (active.includes("facility")) {
    addChipGroup(
      "facilityTypes",
      CONSULTANT_SUMMARY_LABELS.facilityTypes,
      catalogItems("facilityTypes", "facilityType", goal.facilityTypes)
    );
  }

  if (active.includes("retirement")) {
    addChipGroup(
      "retirement",
      CONSULTANT_SUMMARY_LABELS.retirement,
      catalogItems("retirement", "retirementType", goal.retirementType ? [goal.retirementType] : [])
    );
  }

  if (active.length > 0) {
    addChipGroup(
      "motivations",
      CONSULTANT_SUMMARY_LABELS.motivations,
      catalogItems("motivations", "motivation", goal.motivations)
    );
    addChipGroup(
      "timeframe",
      CONSULTANT_SUMMARY_LABELS.timeframe,
      catalogItems("timeframe", "timeframe", goal.timeframe ? [goal.timeframe] : [])
    );
    addChipGroup(
      "clarity",
      CONSULTANT_SUMMARY_LABELS.clarity,
      catalogItems("clarity", "clarity", goal.clarity != null ? [goal.clarity] : [])
    );
  }

  const notes = goal.additionalContext?.trim();
  if (notes) {
    groups.push({
      id: "notes",
      label: CONSULTANT_SUMMARY_LABELS.notes,
      presentation: "note",
      items: [{ id: careerPlanOnboardingGoalItemId("notes", "additionalContext"), label: notes }]
    });
  }

  return groups;
}

export const TALK_IT_OUT_UNSUPPORTED_MESSAGE =
  "Talk it Out is not available in this browser. Type or paste notes instead.";

export function appendDictationTranscript(existing: string, transcript: string): string {
  const next = transcript.replace(/\s+/g, " ").trim();
  if (!next) return existing;
  const current = existing.trimEnd();
  if (!current) return next;
  return `${current} ${next}`;
}

export function dictationErrorMessage(error: string): string | null {
  if (error === "aborted" || error === "no-speech") return null;
  if (error === "not-allowed" || error === "service-not-allowed") {
    return "Microphone access is required for Talk it Out.";
  }
  return "Talk it Out could not capture audio. You can still type or paste notes.";
}

export function careerPlanOnboardingInteractionHeadline(interaction: CareerPlanOnboardingInteraction): string {
  return getInteractionDisplayName({
    owner: interaction.owner,
    mode: interaction.mode as InteractionMode,
    relatedTo: interaction.relatedTo
  });
}

export function careerPlanOnboardingInteractionSummary(interaction: CareerPlanOnboardingInteraction): string {
  const summary = interaction.summary?.trim();
  if (summary) return truncate(summary, { length: 180 });

  const { lastMessage } = interaction;
  if (lastMessage.isPrivate && !lastMessage.haveAccess) return "";
  return truncate(lastMessage.content?.trim() || "", { length: 180 });
}

export function careerPlanOnboardingInteractionDate(interaction: CareerPlanOnboardingInteraction): string {
  return formatInteractionDisplayDate(interaction.interactionDate) ?? "";
}

export function careerPlanOnboardingInteractionCategoryLabel(interaction: CareerPlanOnboardingInteraction): string {
  return interaction.categories[0]?.trim() || "";
}
